import { createColumnHelper, type ColumnDef, type SortingFn } from "@tanstack/react-table";
import type { ReactNode } from "react";

export type FilterKind =
  | "text"
  | "select"
  /** Multi-choice, matching the Vaadin `MultiSelectComboBox` filters. */
  | "multiselect"
  | "number"
  /** A from/to pair, matching `IntegerFromToFilter` / `DoubleFromToFilter`. */
  | "range"
  /** A from/to pair of `YYYY-MM-DD` dates. */
  | "daterange"
  | "none";

export interface FilterOption {
  value: string;
  label: string;
}

/**
 * Per-column configuration the table reads to build the filter row, the column
 * menu and the mobile card layout.
 *
 * `priority` is what makes one column set serve both a 17-column desktop grid
 * and a phone: 1 is the line a user identifies the row by, 2 the supporting
 * line, 3 a meta field worth showing on a card, and anything higher is
 * desktop-only. No screen writes separate mobile markup.
 */
export interface DLColumnMeta {
  filter?: FilterKind;
  filterOptions?: FilterOption[];
  /** Hidden until "show all columns" is picked. Defaults to visible. */
  defaultVisible?: boolean;
  align?: "left" | "right";
  priority?: number;
  /** Translation key for the header, resolved by the table. */
  headerKey?: string;
}

/** `[from, to]`, either side optional — the value a `range` filter holds. */
export type RangeFilterValue = [number | undefined, number | undefined];

/** `[from, to]` of `YYYY-MM-DD` strings — the value a `daterange` filter holds. */
export type DateRangeFilterValue = [string | undefined, string | undefined];

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends unknown, TValue> extends DLColumnMeta {}
}

export interface ColumnOptions extends DLColumnMeta {
  /** Literal header text. Prefer `headerKey` so the header translates. */
  header?: string;
  size?: number;
  enableSorting?: boolean;
  /**
   * Overrides how the column matches its filter value. Needed where the cell
   * holds a set — a product's tags against a multi-select of tag ids.
   *
   * Typed loosely because the option bag is declared once while the builders
   * are generic over the row; the cast at the use site is what re-attaches it.
   */
  filterFn?: TableFn;
  /** Explicit sort comparator, for cells whose accessor value isn't the sort key. */
  sortingFn?: TableFn;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type TableFn = (...args: any[]) => any;

/**
 * Typed column builders, the replacement for the Java `ColumnBuilder` DSL.
 *
 *   const c = columns<ProductDTO>();
 *   const defs = [
 *     c.text("sku", (p) => p.sku, { headerKey: "column.sku", priority: 1 }),
 *     c.number("price", (p) => p.price, { headerKey: "column.price" }),
 *   ];
 */
/**
 * Orders values by what they are, not by what the first row happened to hold.
 *
 * TanStack picks its default comparator from the first row's value: a number
 * gets a numeric sort, anything else the string sort. A column that is
 * numbers except where a row has none -- an empty string from `custom`, a
 * null from `number` -- can therefore sort as text, and 23.2 lands before 3.
 * This looks at the two values being compared instead: numbers numerically,
 * everything else as text with locale rules so 10 follows 9, and a missing
 * value as the smallest of all -- which puts it last on the descending sort
 * a "best sellers" column is read with.
 */
export function compareValues(a: unknown, b: unknown): number {
  const missingA = a === null || a === undefined || a === "";
  const missingB = b === null || b === undefined || b === "";
  if (missingA && missingB) return 0;
  if (missingA) return -1;
  if (missingB) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  const textA = Array.isArray(a) ? a.join(" ") : String(a);
  const textB = Array.isArray(b) ? b.join(" ") : String(b);
  return textA.localeCompare(textB, undefined, { numeric: true, sensitivity: "base" });
}

const byValue: SortingFn<unknown> = (rowA, rowB, columnId) =>
  compareValues(rowA.getValue(columnId), rowB.getValue(columnId));

export function columns<T>() {
  const helper = createColumnHelper<T>();

  const base = (options: ColumnOptions) => ({
    ...(options.header !== undefined ? { header: options.header } : {}),
    ...(options.size !== undefined ? { size: options.size } : {}),
    ...(options.enableSorting !== undefined ? { enableSorting: options.enableSorting } : {}),
    // `as never` is assignable to the generic slot TanStack declares here; the
    // real signature is enforced by the caller's own closure.
    ...(options.filterFn !== undefined ? { filterFn: options.filterFn as never } : {}),
    sortingFn: (options.sortingFn ?? byValue) as never,
    // The comparator places missing values itself; the table must not also
    // move them.
    sortUndefined: false as never,
    meta: {
      filter: options.filter ?? "text",
      ...(options.filterOptions ? { filterOptions: options.filterOptions } : {}),
      ...(options.defaultVisible !== undefined ? { defaultVisible: options.defaultVisible } : {}),
      ...(options.align ? { align: options.align } : {}),
      ...(options.priority !== undefined ? { priority: options.priority } : {}),
      ...(options.headerKey ? { headerKey: options.headerKey } : {}),
    } satisfies DLColumnMeta,
  });

  return {
    /** Plain text, filtered with a case-insensitive contains match. */
    text(id: string, accessor: (row: T) => string | null | undefined, options: ColumnOptions = {}) {
      return helper.accessor((row) => accessor(row) ?? "", {
        id,
        ...base(options),
      }) as ColumnDef<T>;
    },

    /** Right-aligned and tabular, so digits line up down the column. */
    number(id: string, accessor: (row: T) => number | null | undefined, options: ColumnOptions = {}) {
      return helper.accessor((row) => accessor(row) ?? null, {
        id,
        ...base({ filter: "number", align: "right", ...options }),
      }) as ColumnDef<T>;
    },

    /**
     * Anything rendered as a component — badges, thumbnails, two-line cells.
     *
     * The accessor may return an array, which is what a multi-select filter
     * needs when the cell holds a set (a product's tags, its collections).
     */
    custom(
      id: string,
      accessor: (row: T) => string | number | string[] | null | undefined,
      render: (row: T) => ReactNode,
      options: ColumnOptions = {},
    ) {
      return helper.accessor((row) => accessor(row) ?? "", {
        id,
        cell: (context) => render(context.row.original),
        ...base(options),
      }) as ColumnDef<T>;
    },

    /** Row actions. Never sorted, never filtered, never hidden. */
    actions(render: (row: T) => ReactNode, options: ColumnOptions = {}) {
      return helper.display({
        id: "actions",
        cell: (context) => render(context.row.original),
        enableSorting: false,
        enableHiding: false,
        ...base({ filter: "none", align: "right", priority: 99, ...options }),
      }) as ColumnDef<T>;
    },
  };
}
